import {UseQueryResult, useQuery} from 'react-query';

import {RegionalReportResponse, fetchRegionalReport} from 'src/lib/api/regionalReport';
import {UnitSystem} from 'src/lib/api/series';

/**
 * Optional demo fixture escape hatch: when the URL has `?fixture=/demo.json`,
 * the hook fetches the JSON at that path instead of hitting the live API.
 * Intended for sales/demo decks that need a stable frozen snapshot — drop a
 * saved `/api/internal/regional_report` response under
 * `hf-web/public/` and link to it with `?fixture=/...`.
 */
function readFixtureParam(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('fixture');
}

export default function useRegionalReport(
  projectId: string,
  unitSystem: UnitSystem
): UseQueryResult<RegionalReportResponse, Error | null> {
  const fixture = readFixtureParam();
  return useQuery(
    ['regionalReport', projectId, unitSystem, fixture],
    async () => {
      if (fixture) {
        const res = await fetch(fixture);
        if (!res.ok) throw new Error(`Failed to load fixture ${fixture}: ${res.status}`);
        return (await res.json()) as RegionalReportResponse;
      }
      return fetchRegionalReport(projectId, unitSystem);
    },
    {retry: false, refetchOnWindowFocus: false, staleTime: 5 * 60 * 1000}
  );
}
